import React from 'react';
import {Button, FlexRow} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {useReportBuilder} from '../ReportBuilderContext';
import {ReportDownloadMenu} from './ReportDownloadMenu';
import {ReportShareButton} from './ReportShareButton';
import {ReportPeriodControls} from './ReportPeriodControls';

/** Top bar shown in view mode. The reporting period sits on the left, read-only: it is part of what
 * the numbers on screen mean, so a reader needs to see it, but a reader must not be able to change it
 * — see `ReportPeriodControls` for why writing from here would be a defect rather than a feature.
 * Editors additionally see "Share" and "Edit Report" (opens the edit session via `beginEdit()`, which
 * switches `mode` to `'edit'` once the draft is opened); readers only ever see "Download". Download
 * exports the report. Share opens the report-sharing dialog once the report is saved; on an unsaved
 * draft it stays disabled with a tooltip explaining why, since a draft has no Reports-list entry to
 * share. Left-to-right order in the action group is Download, Share, Edit Report, matching the design
 * — the reverse of DOM/visual emphasis order used previously. Download and Share are monochrome
 * (grey-bordered) like every secondary action in both bars; only the one primary action per bar
 * carries the filled high-emphasis treatment. */
export function ViewTopBar(): JSX.Element {
	const {formatMessage} = useIntl();
	const {role, beginEdit} = useReportBuilder();

	return (
		<FlexRow justifyContent={'space-between'} alignItems={'center'} gap={'s'} data-cy={'ai-report-view-top-bar'}>
			<ReportPeriodControls readOnly />
			{/* DS FlexRow defaults to flex:1, so this group would take half the bar and drift inward;
			 * pinned to its content so space-between pushes it to the true right edge, matching EditTopBar. */}
			<FlexRow alignItems={'center'} gap={'s'} flex={'0 0 auto'}>
				<ReportDownloadMenu />
				<ReportShareButton />
				{role === 'editor' ? (
					<Button
						emphasis={'high'}
						icon={'edit'}
						onPress={() => void beginEdit()}
						data-cy={'ai-report-edit-report-button'}
					>
						{formatMessage({id: 'ai_report_builder.edit_report'})}
					</Button>
				) : null}
			</FlexRow>
		</FlexRow>
	);
}
